import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Trash2, RotateCcw } from 'lucide-react';
import { FREQ, WD } from './ChoreBoard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/chores`;
const opts = { withCredentials: true };

export default function ChoreEditor({ onClose, onSaved }) {
  const [items, setItems] = useState(null);
  const [kids, setKids] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(API, opts).then((r) => {
      setKids(r.data.kids || []);
      setItems(r.data.groups.flatMap((g) => g.items).sort((a, b) => a.pos - b.pos).map(({ id, text, freq, kid_id, weekday }) => ({ id, text, freq, kid_id, weekday })));
    });
  }, []);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const edit = (idx, patch) => setItems((xs) => xs.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const remove = (idx) => setItems((xs) => xs.filter((_, i) => i !== idx));
  const add = (freq) => setItems((xs) => [...xs, { text: '', freq }]);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/items`, { items: items.filter((x) => x.text.trim()) }, opts);
      toast.success('Städlistan är sparad 💛');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || 'Kunde inte spara'); }
    setSaving(false);
  };
  const reset = async () => {
    if (!window.confirm('Återställ till bildens städlista? Dina ändringar försvinner.')) return;
    const { data } = await axios.post(`${API}/reset`, {}, opts);
    setItems(data.items);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-[400] bg-black/40 flex items-end sm:items-center justify-center p-2" onClick={onClose} data-testid="chore-editor">
      <div className="bg-white rounded-2xl border-[3px] border-black w-full max-w-lg max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-3 border-b-2">
          <p className="font-black">🧽 Ändra städlistan</p>
          <button onClick={onClose} className="p-1" aria-label="Stäng" data-testid="chore-editor-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto p-3 space-y-3">
          {!items && <p className="text-sm opacity-60">Hämtar…</p>}
          {items && Object.entries(FREQ).map(([f, c]) => (
            <div key={f} className="rounded-xl p-2 border-2" style={{ background: c.bg, borderColor: c.edge, color: c.ink }} data-testid={`chore-editor-group-${f}`}>
              <p className="font-black text-sm mb-1">{c.emoji} {c.label}</p>
              {items.map((x, idx) => x.freq === f && (
                <div key={x.id || `new-${idx}`} className="flex flex-wrap sm:flex-nowrap items-center gap-1 mb-1.5">
                  <input value={x.text} onChange={(e) => edit(idx, { text: e.target.value })} maxLength={120} placeholder="Ny syssla"
                    className="flex-1 min-w-[60%] text-sm rounded-md px-2 py-1 bg-white border border-white focus:border-current outline-none" data-testid={`chore-editor-input-${f}-${idx}`} />
                  {f === 'week' && (
                    <select value={x.weekday ?? ''} onChange={(e) => edit(idx, { weekday: e.target.value === '' ? null : Number(e.target.value) })} title="Fast veckodag"
                      className="text-xs rounded-md px-1 py-1 bg-white border border-white" data-testid={`chore-editor-weekday-${idx}`}>
                      <option value="">Valfri dag</option>
                      {WD.map((d, i) => <option key={d} value={i}>{d}</option>)}
                    </select>
                  )}
                  {kids.length > 0 && (
                    <select value={x.kid_id || ''} onChange={(e) => edit(idx, { kid_id: e.target.value || null })} title="Vem gör den?"
                      className="text-xs rounded-md px-1 py-1 bg-white border border-white" data-testid={`chore-editor-kid-${idx}`}>
                      <option value="">Jag</option>
                      {kids.map((k) => <option key={k.id} value={k.id}>🧸 {k.name}</option>)}
                    </select>
                  )}
                  <button onClick={() => remove(idx)} className="p-1 opacity-60 hover:opacity-100" aria-label="Ta bort" data-testid={`chore-editor-delete-${f}-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={() => add(f)} className="text-xs font-bold flex items-center gap-1 underline" data-testid={`chore-editor-add-${f}`}><Plus className="w-3 h-3" />Lägg till</button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 p-3 border-t-2">
          <button onClick={reset} className="text-xs font-bold flex items-center gap-1 underline opacity-70" data-testid="chore-editor-reset"><RotateCcw className="w-3 h-3" />Bildens lista</button>
          <button onClick={save} disabled={saving || !items} className="neo-btn px-4 py-2 bg-[#C2306B] text-white font-black disabled:opacity-50" data-testid="chore-editor-save">Spara</button>
        </div>
      </div>
    </div>
  );
}
